import { useState, useEffect, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    Dialog,
    DialogTitle,
    IconButton,
    styled,
    CircularProgress,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { DocumentType, uploadDocument } from "../common/redux/documentsAction";

export interface UploadDocumentProps {
    directoryId: string | null;
    onUploadSuccess?: () => void;
}

const UploadDocument = ({
    directoryId,
    onUploadSuccess,
}: UploadDocumentProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [documentName, setDocumentName] = useState("");
    const [documentUri, setDocumentUri] = useState<File | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [uploadError, setUploadError] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>("");
    const [isDragging, setIsDragging] = useState(false);
    const [documentCategory, setDocumentCategory] = useState<DocumentType | "">(
        ""
    );

    const { t } = useTranslation();

    useEffect(() => {
        if (documentUri) {
            const url = URL.createObjectURL(documentUri);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [documentUri]);

    const openDocumentPicker = () => {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "application/pdf, image/png, image/jpeg";
        fileInput.click();

        fileInput.onchange = (e: any) => {
            const file = e?.target?.files[0];
            if (file) {
                setDocumentName(file.name);
                setDocumentUri(file);
            }
        };
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            setDocumentName(file.name);
            setDocumentUri(file);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const upload = async () => {
        if (!documentUri) return;

        if (!directoryId) {
            setUploadError(t("manageDocuments.error.missing.directoryId."));
            return;
        }

        if (!documentCategory) {
            setUploadError(t("manageDocuments.error.upload.missingCategory"));
            return;
        }

        setIsLoading(true);
        setSuccessMessage("");
        setUploadError("");

        try {
            setUploadProgress(50);
            await uploadDocument(documentUri, {
                type: documentCategory,
                name: documentName,
                directoryId: directoryId,
            });
            setIsLoading(false);
            setSuccessMessage(t("profile.document.upload.success"));
            setDocumentUri(null);
            setDocumentName("");
            setDocumentCategory("");
            setUploadProgress(100);
            onUploadSuccess?.();
        } catch (error) {
            console.error("Error uploading document:", error);
            setIsLoading(false);
            setUploadProgress(0);
            setUploadError(t("profile.document.upload.error"));
        }
    };

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isLoading) {
            interval = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 95) return 95;
                    return prev + 1;
                });
            }, 100);
        } else {
            setUploadProgress(0);
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    const documentCategories: DocumentType[] = [
        DocumentType.RESUME,
        DocumentType.DIPLOMA,
        DocumentType.TRAINING_CERTIFICATE,
        DocumentType.CERTIFICATIONS,
        DocumentType.INSURANCE,
        DocumentType.INVOICE,
        DocumentType.QUOTE,
        DocumentType.TIMETABLE,
        DocumentType.OFFICIAL_APPROVAL,
    ];

    return (
        <CardContainer>
            <CardContent
                sx={{
                    padding: "0",
                    "@media(width >= 758px)": {
                        padding: "1rem",
                    },
                }}
            >
                {/* Drag & Drop Area */}
                <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    sx={{
                        borderRadius: 2,
                        padding: 4,
                        textAlign: "center",
                        border: isDragging
                            ? "2px dashed #1976d2"
                            : "2px dashed #90CAF9",
                        backgroundColor: isDragging ? "#e1f5fe" : "#E3F2FD",
                        cursor: "pointer",
                        mb: 2,
                    }}
                    onClick={openDocumentPicker}
                >
                    <CloudUploadIcon color="primary" sx={{ fontSize: 48 }} />
                    <Typography variant="body2" color="textSecondary">
                        {t("manageDocuments.documentUpload.dragDrop")}
                    </Typography>
                    {documentName && (
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            {documentName}
                        </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                        {documentUri?.type} •{" "}
                        {documentUri?.size !== undefined
                            ? (documentUri.size / 1024 / 1024).toFixed(2)
                            : "0.00"}{" "}
                        MB
                    </Typography>
                </Box>

                {documentUri && (
                    <>
                        <PreviewBox>
                            <ButtonSeePreview
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setShowPreview(true)}
                            >
                                {t("profile.document.preview")}
                            </ButtonSeePreview>

                            <StyledFormControl fullWidth>
                                <InputLabel id="document-category-label">
                                    {t(
                                        "documentsLabel.select.category.formLabel"
                                    )}
                                </InputLabel>
                                <Select
                                    labelId="document-category-label"
                                    value={documentCategory}
                                    onChange={(e) =>
                                        setDocumentCategory(
                                            e.target.value as DocumentType
                                        )
                                    }
                                    required
                                >
                                    {documentCategories.map((category) => (
                                        <MenuItem
                                            key={category}
                                            value={category}
                                            sx={{
                                                fontSize: "14px",
                                                height: 36,
                                            }}
                                        >
                                            {t(
                                                `manageDocuments.categories.${category}`
                                            ) || category}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </StyledFormControl>
                        </PreviewBox>
                        {isLoading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    marginTop: "1rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                <CircularProgress size={22} color="success" />
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {t("manageDocuments.upload.inProgress") ||
                                        "Uploading..."}
                                    {uploadProgress > 0 &&
                                        ` ${uploadProgress}%`}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: "rgb(21, 94, 149)",
                        ":hover": {
                            backgroundColor: "rgb(21, 94, 149)",
                        },
                    }}
                    endIcon={
                        successMessage ? (
                            <CheckCircleIcon />
                        ) : (
                            <UploadFileIcon />
                        )
                    }
                    disabled={!documentUri || isLoading || !!successMessage}
                    onClick={upload}
                    fullWidth
                >
                    {successMessage
                        ? t("profile.document.upload.success")
                        : t("button.send.document")}
                </Button>
            </CardContent>

            {/* Preview Dialog */}
            <Dialog
                open={showPreview}
                onClose={() => setShowPreview(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {documentName}
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowPreview(false)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Box sx={{ p: 2 }}>
                    {documentUri && documentUri.type.startsWith("image/") && (
                        <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ maxWidth: "100%", maxHeight: "70vh" }}
                        />
                    )}
                    {documentUri && documentUri.type === "application/pdf" && (
                        <iframe
                            src={previewUrl}
                            style={{ width: "100%", height: "70vh" }}
                            title="PDF Preview"
                        />
                    )}
                </Box>
            </Dialog>
            <Snackbar
                open={!!successMessage}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Alert
                    onClose={() => setSuccessMessage("")}
                    severity="success"
                    sx={{ width: "100%" }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!uploadError}
                autoHideDuration={3000}
                onClose={() => setUploadError("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Alert
                    onClose={() => setUploadError("")}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {uploadError}
                </Alert>
            </Snackbar>
        </CardContainer>
    );
};

export default UploadDocument;

const CardContainer = styled(Card)({
    width: "100%",
    maxWidth: 640,
    margin: "auto",
    borderRadius: "5px",
    backgroundColor: "#fdfdfd",
});

const PreviewBox = styled(Stack)({
    flexDirection: "column",
    alignItems: "start",
    gap: "1rem",
    marginBottom: "1rem",
    justifyContent: "center",

    "@media(width >= 768px)": {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
});

const ButtonSeePreview = styled(Button)({
    width: "100%",
    maxWidth: 320,
    height: "40px",

    "@media(width >= 768px)": {
        maxWidth: 140,
    },
});

const StyledFormControl = styled(FormControl)({
    height: 40,
    fontSize: "14px",
    "& .MuiInputBase-root": {
        height: 40,
        fontSize: "14px",
    },
    "& .MuiInputLabel-root": {
        fontSize: "14px",
        top: "-5px",
    },
    "& .MuiSelect-select": {
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        height: 40,
    },
    "& .MuiSvgIcon-root": {
        fontSize: "20px",
    },
});
